import PropTypes from 'prop-types'
import { useRef } from 'react'
import styled from 'styled-components'
import { Base64 } from 'js-base64'
import { useTranslation } from 'react-i18next'

import colors from 'components/utils/colors'

import FileInput from 'components/Shared/Form/FileInput/Index'
import Icon from 'components/Shared/Icon/Index'
import IconLink from 'components/Shared/IconLink/Index'

const StyledImagePicker = styled.div`
  display: flex;
  gap: 16px;
`

const ImageHolder = styled.div`
  border: 1px solid ${colors.secondary70};
  border-radius: 4px;
  width: 70px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledImage = styled.img`
  width: 56px;
  height: 56px;
`

const RecommendationsTitle = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary20};
  line-height: 16px;
`

const RecommendationsLine = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 12px;
`

const RecommendationsParam = styled.div`
  display: flex;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
`

const RecommendationsParamTitle = styled.label`
  color: ${colors.secondary40};
`

const RecommendationsParamText = styled.label`
  font-weight: 600;
  color: ${colors.secondary20};
`

const acceptedTypes = '.png, .svg'

const ImagePicker = ({
  base64Content, contentType, filename, onChange,
  recommendationSize, uploadButtonTitle, updateButtonTitle,
}) => {
  const inputRef = useRef(null)
  const { t } = useTranslation()

  let img, imageElement, uploadIconName, uploadIconText

  if (base64Content && contentType) {
    img = `data:${contentType};charset=utf-8;base64,${base64Content}`
    imageElement = <StyledImage src={img} />
    uploadIconName = 'icon-refetch'
    uploadIconText = updateButtonTitle || t('imagePicker.updateLogo')
  } else {
    imageElement = <Icon iconName='icon-logo-nodata' color={colors.secondary40} />
    uploadIconName = 'icon-upload'
    uploadIconText = uploadButtonTitle || t('imagePicker.uploadLogo')
  }

  const onUploadButtonClick = (e) => {
    inputRef.current.click()
  }

  const onImageFileSelect = async (event) => {
    const file = event.target.files[0]

    if (!file) {
      return
    }

    const buf = await file.arrayBuffer()
    const base64Content = Base64.fromUint8Array(new Uint8Array(buf))

    onChange({
      base64Content: base64Content,
      contentType: file.type,
      filename: file.name,
    })
  }

  return (
    <StyledImagePicker>
      <ImageHolder data-test='image-holder'>{imageElement}</ImageHolder>
      <div>
        <RecommendationsTitle>{t('imagePicker.recommendationsTitle')}</RecommendationsTitle>
        <RecommendationsLine>
          <RecommendationsParam>
            <RecommendationsParamTitle>{t('imagePicker.size')}:</RecommendationsParamTitle>
            <RecommendationsParamText>{recommendationSize || '128x128px'}</RecommendationsParamText>
          </RecommendationsParam>
          <RecommendationsParam>
            <RecommendationsParamTitle>{t('imagePicker.formats')}:</RecommendationsParamTitle>
            <RecommendationsParamText>svg/png</RecommendationsParamText>
          </RecommendationsParam>
        </RecommendationsLine>

        <IconLink
          iconName={uploadIconName}
          text={uploadIconText}
          onClick={onUploadButtonClick}
        />
      </div>

      <FileInput
        ref={inputRef}
        onChange={onImageFileSelect}
        accept={acceptedTypes}
      />
    </StyledImagePicker>
  )
}

ImagePicker.propTypes = {
  base64Content: PropTypes.string,
  contentType: PropTypes.string,
  filename: PropTypes.string,
  onChange: PropTypes.func,
  recommendationSize: PropTypes.string,
  uploadButtonTitle: PropTypes.string,
  updateButtonTitle: PropTypes.string,
}

export default ImagePicker
